import { Tooltip } from "primereact/tooltip";
import AutoPayIcon from "app/assets/IconComponent/AutoPayIcon";

// ── Types ─────────────────────────────────────────────────────────────────────

// Shape returned by the funding API's `ledger.rows` (KP / BS).
export interface ApiLedgerRow {
  insured: { name?: string; profilePicture?: string | null };
  insuranceCompany?: {
    name?: string;
    logoPath?: string | null;
    insuranceCompanyBrandings?: {
      primaryLogo?: string | null;
      primaryLogoDark?: string | null;
      primaryColor?: string;
      primaryColorDark?: string;
    } | null;
  } | null;
  policyNumber?: string;
  type?: string;
  benefit?: number | string;
  expiration?: number;
  method?: string;
  mode?: string;
  amount?: number | string;
  annualizedPremium?: number | string;
  autoPay?: boolean;
}

interface FundingLedgerProps {
  rows?: ApiLedgerRow[];
  totalBenefit?: number;
  totalAnnualizedPremium?: number;
  isDark?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const toNum = (v: number | string | null | undefined): number =>
  typeof v === "number" ? v : parseFloat(String(v ?? "")) || 0;

// Benefit / Total Benefit → thousands(K) / millions(M) / billions(B).
const fmtCompact = (value: number | string): string => {
  const v = toNum(value);
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${Math.round(v / 1_000)}K`;
  return `$${v.toLocaleString("en-US")}`;
};

// Amount / Annualized → full currency.
const fmtCurrency = (value: number | string): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(toNum(value));

// Method: abbreviation + full form (shown on hover).
const METHOD_MAP: Record<string, { abbr: string; full: string }> = {
  IAPP_FACILITATED: { abbr: "IF", full: "IAPP Facilitated" },
  EXTERNALLY_DIRECTED: { abbr: "ED", full: "Externally Directed" },
};

// Mode: abbreviation + full form (shown on hover).
const MODE_MAP: Record<string, { abbr: string; full: string }> = {
  MONTHLY: { abbr: "M", full: "Monthly" },
  QUARTERLY: { abbr: "Q", full: "Quarterly" },
  SEMI_ANNUALLY: { abbr: "SA", full: "Semi-Annually" },
  ANNUALLY: { abbr: "A", full: "Annually" },
};

const fmtExpiration = (years?: number): string => {
  if (years == null) return "—";
  return `${years} Yr${years === 1 ? "" : "s"}`;
};

const firstLetter = (name?: string): string => (name?.trim()?.[0] ?? "").toUpperCase() || "—";

// ── Sub-components ────────────────────────────────────────────────────────────

// Profile picture, or a neutral-gray circle with the first letter of the name.
const Avatar = ({ name, picture }: { name?: string; picture?: string | null }) =>
  picture ? (
    <img
      src={picture}
      alt={name}
      title={name}
      style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
    />
  ) : (
    <div
      title={name}
      className="rounded-full bg-[#E5E7EB] dark:bg-[#3A3A50] text-[#64748B] dark:text-gray-300"
      style={{
        width: 34,
        height: 34,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 14,
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {firstLetter(name)}
    </div>
  );

// Insurer column: a single circle ringed with the brand color (primaryColor /
// primaryColorDark from the API). Shows the branding logo, or — when there's no
// theme logo (e.g. no dark logo) — the first letter of the name on a brand-color
// fill, mirroring PolicyInformationCard's badge.
const InsurerBadge = ({
  name,
  brandingLogo,
  color,
}: {
  name?: string;
  brandingLogo?: string | null;
  color?: string | null;
}) => {
  const ringColor = color ?? "#22C55E";
  return (
    <div
      title={name}
      style={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        border: `2px solid ${ringColor}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        boxSizing: "border-box",
      }}
    >
      {brandingLogo ? (
        <img
          src={brandingLogo}
          alt={name ?? "insurer branding"}
          style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }}
        />
      ) : (
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            backgroundColor: ringColor,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {firstLetter(name)}
        </div>
      )}
    </div>
  );
};

const PolicyDot = () => (
  <span
    style={{
      width: 8,
      height: 8,
      borderRadius: "50%",
      backgroundColor: "#22C55E",
      display: "inline-block",
      flexShrink: 0,
    }}
  />
);

const AmountBadge = ({
  value,
  method,
  autoPay,
}: {
  value: number | string;
  method?: string;
  autoPay?: boolean;
}) => {
  const isIAPPFacilitated = method === "IAPP_FACILITATED";
  const fillColor = !isIAPPFacilitated ? "#D1D5DB" : autoPay ? "#1DA750" : "#6B7280";
  const showGreenBg = isIAPPFacilitated && !!autoPay;
  const tooltipText = !isIAPPFacilitated
    ? "Externally Directed"
    : autoPay
    ? "Auto-pay ON"
    : "Auto-pay OFF";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontWeight: 500 }}>{fmtCurrency(value)}</span>
      <div
        className="autopay-badge-tooltip"
        data-pr-tooltip={tooltipText}
        style={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          backgroundColor: showGreenBg ? "#1DA750" : "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <AutoPayIcon fill={fillColor} />
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const HEADER_CLS = "text-[11px] font-semibold uppercase tracking-wider text-gray-400 text-left";
const CELL_CLS = "py-3 text-sm text-gray-700 dark:text-gray-200";

export default function FundingLedger({
  rows = [],
  totalBenefit,
  totalAnnualizedPremium,
  isDark,
}: FundingLedgerProps) {
  const cardBg = isDark ? "bg-[#2A2A3D]" : "bg-white";
  const dividerColor = isDark ? "#3A3A50" : "#F3F4F6";

  // Fall back to summing the rows if the API didn't send totals.
  const benefitTotal = totalBenefit ?? rows.reduce((s, r) => s + toNum(r.benefit), 0);
  const annualizedTotal =
    totalAnnualizedPremium ?? rows.reduce((s, r) => s + toNum(r.annualizedPremium), 0);

  return (
    <div className={`rounded-2xl shadow-md overflow-hidden ${cardBg} px-5 pt-4 pb-3`}>
      <Tooltip key={rows.length} target=".autopay-badge-tooltip" position="top" />
      <div className="overflow-x-auto">
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 750 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${dividerColor}` }}>
              {[
                "Insured",
                "Insurer",
                "Policy #",
                "Type",
                "Benefit",
                "Expiration",
                "Method",
                "Mode",
                "Amount",
                "Annualized",
              ].map((h) => (
                <th key={h} className={HEADER_CLS} style={{ paddingRight: 16, paddingBottom: 10 }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td className={`${CELL_CLS} text-gray-400`} colSpan={10}>
                  No policies to display.
                </td>
              </tr>
            ) : (
              rows.map((row, i) => {
                const branding = row.insuranceCompany?.insuranceCompanyBrandings;
                // Theme-specific logo only — when the dark logo is missing in
                // dark mode, the badge shows the first letter instead.
                const brandingLogo = isDark ? branding?.primaryLogoDark : branding?.primaryLogo;
                const insurerColor =
                  (isDark ? branding?.primaryColorDark : branding?.primaryColor) ??
                  branding?.primaryColor;
                const method = METHOD_MAP[row.method ?? ""];
                const mode = MODE_MAP[row.mode ?? ""];
                return (
                  <tr
                    key={`${row.policyNumber}-${i}`}
                    style={{
                      borderBottom: i < rows.length - 1 ? `1px solid ${dividerColor}` : "none",
                    }}
                  >
                    <td className={CELL_CLS} style={{ paddingRight: 12 }}>
                      <Avatar name={row.insured?.name} picture={row.insured?.profilePicture} />
                    </td>
                    <td className={CELL_CLS} style={{ paddingRight: 12 }}>
                      <InsurerBadge
                        name={row.insuranceCompany?.name}
                        brandingLogo={brandingLogo}
                        color={insurerColor}
                      />
                    </td>
                    <td className={CELL_CLS} style={{ paddingRight: 16 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <PolicyDot />
                        {row.policyNumber}
                      </div>
                    </td>
                    <td className={`${CELL_CLS} font-bold`} style={{ paddingRight: 16 }}>
                      {row.type}
                    </td>
                    <td className={CELL_CLS} style={{ paddingRight: 16 }}>
                      {fmtCompact(row.benefit ?? 0)}
                    </td>
                    <td className={CELL_CLS} style={{ paddingRight: 16 }}>
                      {fmtExpiration(row.expiration)}
                    </td>
                    <td className={CELL_CLS} style={{ paddingRight: 16 }}>
                      <span title={method?.full ?? row.method}>{method?.abbr ?? row.method}</span>
                    </td>
                    <td className={CELL_CLS} style={{ paddingRight: 16 }}>
                      <span title={mode?.full ?? row.mode}>{mode?.abbr ?? row.mode}</span>
                    </td>
                    <td className={CELL_CLS} style={{ paddingRight: 16 }}>
                      <AmountBadge
                        value={row.amount ?? 0}
                        method={row.method}
                        autoPay={row.autoPay}
                      />
                    </td>
                    <td className={CELL_CLS}>{fmtCurrency(row.annualizedPremium ?? 0)}</td>
                  </tr>
                );
              })
            )}
          </tbody>

          {/* Totals — aligned under the Benefit and Annualized columns. */}
          <tfoot>
            <tr style={{ borderTop: `1px solid ${dividerColor}` }}>
              <td colSpan={4} style={{ textAlign: "right", paddingRight: 16, paddingTop: 14 }}>
                <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                  Total Benefit
                </span>
              </td>
              <td style={{ paddingRight: 16, paddingTop: 14 }}>
                <span className="text-2xl font-bold text-gray-800 dark:!text-white">
                  {fmtCompact(benefitTotal)}
                </span>
              </td>
              <td colSpan={4} style={{ textAlign: "right", paddingRight: 16, paddingTop: 14 }}>
                <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                  Total Annualized Premium
                </span>
              </td>
              <td style={{ paddingTop: 14 }}>
                <span className="text-2xl font-bold text-gray-800 dark:!text-white">
                  {fmtCurrency(annualizedTotal)}
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
